import type { ReactNode, JSX, ReactElement } from 'react';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import type { Nilable } from '~/utilities/type-guards';
import {
  isNil,
  isNonEmptyArray,
  isNonEmptyString,
  isNotNil,
  isNotUndefined,
} from '~/utilities/type-guards';
import type { StorageOptions, SupportedFeatureIds } from '~/onboarding/types';
import { activeOnboarding } from '~/onboarding/activeOnboarding';
import type { StorageService } from '~/onboarding/services/useStorageServices';
import { useStorageServices } from '~/onboarding/services/useStorageServices';
import { useForceUpdate } from '@wistia/vhs';
import { createPortal } from 'react-dom';
import { OnboardingContext } from './OnboardingContext';
import { SnowplowContext } from '../useSnowplow/SnowplowContext';

export type BackfillUtils = { getItem: () => Nilable<unknown>; removeItem: () => void };

export type OnboardingOptions = {
  allAtOnce?: boolean; // Display all steps at once or in succession. Default: false
  hideOnUnmount?: boolean; // Whether or not to hide/end the onboarding experience when useOnboarding unmounts. Default: true
  showImmediately?: boolean; // Show the onboarding experience immediately when `configure` is called. Default: false
  storage: StorageOptions; // How data should be stored
  isEligible?: () => Promise<boolean>; // Whether or not the current user/contact/account/etc is eligible to see this experience. Default: true
  backfillUtils?: BackfillUtils;
};

const DEFAULT_OPTIONS: Pick<OnboardingOptions, 'hideOnUnmount'> = {
  hideOnUnmount: true,
};

export type Experience = OnboardingOptions &
  Pick<OnboardingConfiguration, 'stepIds' | 'steps'> & {
    storageService: StorageService;
  };

export type OnboardingConfiguration = {
  featureId: SupportedFeatureIds;
  steps?: ReactElement[];
  options: OnboardingOptions;
  // TODO: Make stepIds required once all onboarding configs are updated
  stepIds?: string[];
};

export const OnboardingProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const experiencesRef = useRef<Partial<Record<SupportedFeatureIds, Experience>>>({});
  const currentFeatureIdRef = useRef<SupportedFeatureIds | undefined>(undefined);
  const [currentIndex, setCurrentIndex] = useState<number>(-1);
  const forceUpdate = useForceUpdate();

  const storageServices = useStorageServices();
  const storageServiceByOption: Record<StorageOptions['type'], StorageService> = useMemo(
    () => ({
      none: storageServices.none,
      server: storageServices.server,
    }),
    [storageServices.none, storageServices.server],
  );

  const getExperienceById = useCallback((id?: SupportedFeatureIds) => {
    return isNonEmptyString(id) && id in experiencesRef.current ? experiencesRef.current[id] : null;
  }, []);

  const getCurrentExperience = useCallback(
    () => getExperienceById(currentFeatureIdRef.current),
    [getExperienceById],
  );

  const getCurrentStepId = useCallback(
    () => getCurrentExperience()?.stepIds?.[currentIndex],
    [getCurrentExperience, currentIndex],
  );

  const tryBackfill = useCallback(
    async (experience: Experience, featureId: SupportedFeatureIds) => {
      const { storageService, backfillUtils } = experience;

      if (isNil(backfillUtils)) {
        return false;
      }

      const backfillResponse = await storageService.backfillOnboardingActivity?.(
        featureId,
        backfillUtils,
      );

      return backfillResponse?.backfilled;
    },
    [],
  );

  const shouldShowCurrentExperience = useCallback(
    async (featureId: SupportedFeatureIds) => {
      try {
        const experience = getExperienceById(featureId);

        if (isNil(experience)) {
          return false;
        }

        if (await tryBackfill(experience, featureId)) {
          return false;
        }

        if (isNotUndefined(experience.isEligible)) {
          const isEligible = await experience.isEligible();
          if (!isEligible) {
            return false;
          }
        }

        const activities = await experience.storageService.getOnboardingActivities(featureId);
        // If there are no activities, we should show the experience
        return !activities || activities.length === 0;
      } catch (_error) {
        throw Error(`featureId '${featureId}' does not exist`);
      }
    },
    [getExperienceById, tryBackfill],
  );

  const hasConflict = useCallback(() => isNotNil(currentFeatureIdRef.current), []);

  const show = useCallback(
    async (featureId: SupportedFeatureIds) => {
      const shouldShow = await shouldShowCurrentExperience(featureId);

      if (!shouldShow) {
        // TODO: need to update to log message here explaining why show doesn't work
        // https://app.shortcut.com/wistia-pde/story/34952/expand-uselogger-hook-so-it-can-be-used-in-onboardingprovider

        // eslint-disable-next-line no-console
        console.warn(`Skipping show ${featureId}`);
        return;
      }

      if (featureId in experiencesRef.current) {
        if (hasConflict()) {
          // eslint-disable-next-line no-console
          console.warn(
            `Another onboarding experience is already shown: ${currentFeatureIdRef.current}. Skipping show for: ${featureId}`,
          );
          return;
        }

        currentFeatureIdRef.current = featureId;
        setCurrentIndex(0);

        const experience = getExperienceById(featureId);

        if (isNotNil(experience) && experience.storage.activities?.experience.view === 'start') {
          void experience.storageService.createOnboardingActivity('view', featureId);
        }
        forceUpdate();
      } else {
        throw Error(`featureId '${featureId}' does not exist`);
      }
    },
    [getExperienceById, hasConflict, shouldShowCurrentExperience, forceUpdate],
  );

  const hide = useCallback(
    (featureId: SupportedFeatureIds) => {
      if (featureId in experiencesRef.current) {
        setCurrentIndex(-1); // Set the index to -1 so that we render steps[-1] (undefined)
        currentFeatureIdRef.current = undefined;
      }
      // Hiding experiences that don't exist is not a bug and can be expected if
      // this is called in a useEffect where effects can run out of order
    },
    [setCurrentIndex],
  );

  const configure = useCallback(
    ({ featureId, steps, options, stepIds }: OnboardingConfiguration) => {
      if (isNotNil(experiencesRef.current[featureId])) {
        return;
      }
      const newExperience = {
        ...DEFAULT_OPTIONS,
        ...options,
        steps,
        storageService: storageServiceByOption[options.storage.type],
        stepIds,
      };

      // For server storage we default to saving the view event at the start of the experience
      if (newExperience.storage.type === 'server' && isNil(newExperience.storage.activities)) {
        newExperience.storage.activities = {
          experience: { view: 'start' },
        };
      }

      experiencesRef.current[featureId] = newExperience;

      if (newExperience.showImmediately) {
        void show(featureId);
      }
    },
    [show, storageServiceByOption],
  );

  const next = useCallback(
    (featureId: SupportedFeatureIds) => {
      const experience = getExperienceById(featureId);
      if (!experience || experience.allAtOnce) {
        return;
      }

      const { steps, stepIds } = experience;
      const length = stepIds?.length ?? steps?.length ?? 0;
      setCurrentIndex((current) => Math.min(current + 1, length - 1));
    },
    [getExperienceById],
  );

  const prev = useCallback(
    (featureId: SupportedFeatureIds) => {
      const experience = getExperienceById(featureId);
      if (!experience || experience.allAtOnce) {
        return;
      }

      setCurrentIndex((current) => Math.max(current - 1, 0));
    },
    [getExperienceById],
  );

  const value = useMemo(
    () => ({
      configure,
      show,
      next,
      prev,
      setCurrentIndex,
      currentIndex,
      getCurrentExperience,
      getCurrentFeatureId: () => currentFeatureIdRef.current,
      getCurrentStepId,
      hide,
    }),
    [configure, show, next, prev, currentIndex, hide, getCurrentExperience, getCurrentStepId],
  );

  useEffect(() => {
    const featureId = currentFeatureIdRef.current;

    if (isNotNil(featureId)) {
      const experience = getExperienceById(featureId);

      if (isNotNil(experience) && isNotNil(experience.steps)) {
        const lastStep = experience.steps.length - 1;

        if (currentIndex === lastStep && experience.storage.activities?.experience.view === 'end') {
          void experience.storageService.createOnboardingActivity('view', featureId);
        }
      }
    }
  }, [currentIndex, getExperienceById]);

  const snowplowValue = useMemo(
    () => ({
      label: isNil(currentFeatureIdRef.current)
        ? undefined
        : activeOnboarding[currentFeatureIdRef.current].id,
      step: getCurrentExperience()?.allAtOnce ? undefined : currentIndex,
    }),
    [currentIndex, getCurrentExperience],
  );

  let renderedOnboarding: ReactElement | ReactElement[] | null = null;
  const currentExperience = getCurrentExperience();
  if (currentExperience) {
    const { allAtOnce, steps } = currentExperience;
    if (isNonEmptyArray(steps) && isNotNil(steps[currentIndex])) {
      renderedOnboarding = allAtOnce ? steps : steps[currentIndex];
    }
  }

  return (
    <OnboardingContext.Provider value={value}>
      {children}
      <SnowplowContext.Provider value={snowplowValue}>
        {isNotNil(renderedOnboarding) ? createPortal(renderedOnboarding, document.body) : null}
      </SnowplowContext.Provider>
    </OnboardingContext.Provider>
  );
};
